/**
 * The daily dispatch sheet (דף שילוח / פקודת יום) — the single source of truth
 * for what a printed dispatch order contains and how it is grouped.
 *
 * Pure, like every other engine in `src/core/`: the report is built from lists
 * the screen already holds, so `DailyDispatchReport.tsx` only has to draw the
 * rows it is handed, and the healthcheck can build the very same sheet without
 * a browser.
 *
 * Dates are the `YYYY-MM-DD` keys tasks already store, compared as strings —
 * a range filter is therefore two string comparisons and never a time-zone
 * question.
 */
import { contactOf, formatTaskDate, formatTaskWindow, sortTasks, taskTypeName } from './task-filters';
import { teamHeadcount } from './assignment-rules';
import { vehicleCallSign } from './military-id';
import { soldierRoleLabel } from './soldier-roles';
import { describeVehicleLocation } from './vehicle-location';
import { CREW_ROLE_LABELS, ESCORT_OPTION_LABELS } from '../types/assignment';
import type { CrewRole, ExternalPerson, TaskTeam } from '../types/assignment';
import { VEHICLE_TYPE_LABELS, WEIGHT_CATEGORY_SHORT_LABELS } from '../types/fleet';
import type { Soldier, Vehicle } from '../types/fleet';
import { TASK_PRIORITY_LABELS, normalizeTaskStatus, taskStatusLabel } from '../types/task';
import type { Task, TaskPriority, TaskType } from '../types/task';
import type { Contact } from '../types/contact';

// ── Grouping ─────────────────────────────────────────────────────────

export const DISPATCH_GROUPINGS = ['type', 'time'] as const;

export type DispatchGrouping = (typeof DISPATCH_GROUPINGS)[number];

export const DISPATCH_GROUPING_LABELS: Record<DispatchGrouping, string> = {
  type: 'לפי סוג משימה',
  time: 'לפי שעות היום',
};

export const DEFAULT_DISPATCH_GROUPING: DispatchGrouping = 'time';

/** The four parts of the operational day a dispatch sheet is read in. */
export const DAY_PARTS = ['morning', 'noon', 'evening', 'night'] as const;

export type DayPart = (typeof DAY_PARTS)[number];

export const DAY_PART_LABELS: Record<DayPart, string> = {
  morning: 'בוקר · 06:00–11:59',
  noon: 'צהריים · 12:00–17:59',
  evening: 'ערב · 18:00–21:59',
  night: 'לילה · 22:00–05:59',
};

/** Which part of the day a `HH:MM` start time falls in. */
export function dayPartOf(startTime: string | null | undefined): DayPart {
  const hour = Number(String(startTime ?? '').slice(0, 2));
  if (!Number.isFinite(hour)) return 'morning';
  if (hour >= 6 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'noon';
  if (hour >= 18 && hour < 22) return 'evening';
  return 'night';
}

// ── The date range ───────────────────────────────────────────────────

/** An inclusive span of calendar days. An empty bound is "no bound". */
export interface DispatchRange {
  from: string;
  to: string;
}

export const EMPTY_DISPATCH_RANGE: DispatchRange = { from: '', to: '' };

/** One single day, the way the sheet is printed most of the time. */
export function dayRange(dateKey: string): DispatchRange {
  return { from: dateKey, to: dateKey };
}

/** Orders the two bounds, so a range typed backwards still selects the days. */
export function normalizeRange(
  from: string | null | undefined,
  to: string | null | undefined,
): DispatchRange {
  const start = String(from ?? '').trim();
  const end = String(to ?? '').trim();
  if (start && end && end < start) return { from: end, to: start };
  return { from: start, to: end };
}

/** True when the task's own day falls inside the (inclusive) range. */
export function isInRange(task: Task, range: DispatchRange): boolean {
  const { from, to } = normalizeRange(range.from, range.to);
  if (from && task.date < from) return false;
  if (to && task.date > to) return false;
  return true;
}

export interface DispatchFilterOptions {
  /** A cancelled task is left off the sheet unless it is asked for. */
  includeCancelled?: boolean;
}

/** The tasks a sheet covers, chronologically. */
export function filterDispatchTasks(
  tasks: readonly Task[],
  range: DispatchRange = EMPTY_DISPATCH_RANGE,
  options: DispatchFilterOptions = {},
): Task[] {
  return sortTasks(
    tasks.filter((task) => {
      if (!options.includeCancelled && normalizeTaskStatus(task.statusId) === 'cancelled') {
        return false;
      }
      return isInRange(task, range);
    }),
  );
}

/** `05/06` or `05/06–07/06` — the range as the sheet header prints it. */
export function formatRangeLabel(range: DispatchRange): string {
  const { from, to } = normalizeRange(range.from, range.to);
  const short = (key: string): string => {
    const [, month, day] = key.split('-');
    return month && day ? `${day}/${month}` : key;
  };
  if (!from && !to) return 'כל התאריכים';
  if (from && !to) return `מ-${short(from)}`;
  if (!from && to) return `עד ${short(to)}`;
  if (from === to) return short(from);
  return `${short(from)}–${short(to)}`;
}

// ── The rows ─────────────────────────────────────────────────────────

/** What a crew line reads as when the roster holds no licence for the soldier. */
export const NO_LICENSE_LABEL = 'ללא רישיון';

/** One person on the sheet, roster soldier or manually added rider. */
export interface DispatchCrewLine {
  crewRole: CrewRole;
  roleLabel: string;
  name: string;
  personalNumber?: string;
  phone?: string;
  /** The job the soldier holds in the company (נהג, מ"פ …) — '' for a rider. */
  jobLabel: string;
  /**
   * Licence weight class, short form (`בינוני`) — the "דרגת רישיון" a dispatch
   * order names beside a driver. `''` for an external rider, whose licence the
   * unit does not hold.
   */
  licenseLabel: string;
  /** True for a rider typed into the team form rather than picked from the roster. */
  external: boolean;
  /**
   * The vehicle an external person arrived in, when they brought one. Recorded
   * on the person and never in the fleet, so a one-day loan reaches the order
   * without reaching the vehicle roster. Both are `''` for a roster soldier.
   */
  vehicleModel: string;
  plateNumber: string;
}

/** One vehicle line of a task — the row a driver actually reads. */
export interface DispatchTeamLine {
  teamId: string;
  vehicleId: string;
  /** The three-digit internal number, printed bold on the sheet. */
  vehicleMilitaryId: string;
  vehicleName: string;
  /** The model as the licence writes it — the "דגם" line of the order. */
  vehicleModel: string;
  plateNumber: string;
  vehicleTypeLabel: string;
  /** The company physically holding the vehicle — its "פלוגה" on the order. */
  vehicleCompany: string;
  driver: DispatchCrewLine | null;
  escort: DispatchCrewLine | null;
  escortLabel: string;
  passengers: DispatchCrewLine[];
  crewCount: number;
  teamNote?: string;
}

/** One task on the sheet, with every vehicle riding for it. */
export interface DispatchRow {
  taskId: string;
  title: string;
  typeName: string;
  date: string;
  dateLabel: string;
  window: string;
  startTime: string;
  dayPart: DayPart;
  /** The urgency itself — the board filters on it; the order never prints it. */
  priority: TaskPriority;
  priorityLabel: string;
  statusLabel: string;
  destination: string;
  destinationLink: string;
  contactName: string;
  contactPhone: string;
  contactRole: string;
  operationalNotes: string;
  teams: DispatchTeamLine[];
  vehicleCount: number;
  crewCount: number;
}

export interface DispatchGroup {
  key: string;
  title: string;
  rows: DispatchRow[];
}

export interface DispatchTotals {
  tasks: number;
  vehicles: number;
  crew: number;
}

export interface DispatchReport {
  range: DispatchRange;
  rangeLabel: string;
  groupBy: DispatchGrouping;
  groups: DispatchGroup[];
  rows: DispatchRow[];
  totals: DispatchTotals;
}

export interface DispatchInput {
  tasks: readonly Task[];
  /** Every vehicle team of the tasks handed in — flat, grouped here by task. */
  teams?: readonly TaskTeam[];
  vehicles?: readonly Vehicle[];
  soldiers?: readonly Soldier[];
  contacts?: readonly Contact[];
  taskTypes?: readonly TaskType[];
  range?: DispatchRange;
  groupBy?: DispatchGrouping;
  includeCancelled?: boolean;
  /** Fixed evaluation moment, so "היום" on the sheet does not drift. */
  now?: number;
}

const text = (value: string | null | undefined): string => String(value ?? '').trim();

// ── Naming a person on an order ──────────────────────────────────────

/**
 * How a personal number is written wherever an order names a person. A driver
 * is signed for by their מ.א., not by their name — two soldiers share a name,
 * nobody shares a personal number.
 */
export const PERSONAL_ID_PREFIX = 'מ.א.';

/** The fields any crew row — roster soldier or typed-in rider — is named by. */
export interface CrewIdentityFields {
  name: string;
  personalNumber?: string | null;
  phone?: string | null;
}

/** `מ.א. 5374154`, or `''` when the roster holds no personal number. */
export function personalIdLabel(personalNumber: string | null | undefined): string {
  const digits = text(personalNumber);
  return digits ? `${PERSONAL_ID_PREFIX} ${digits}` : '';
}

/**
 * The two numbers a person is reached and signed for by, in the order a written
 * dispatch order prints them: `מ.א. 5374154 | 052-8892893`. An unknown number is
 * dropped rather than printed as a dash, so a rider the unit holds no מ.א. for
 * reads as their phone alone.
 */
export function crewIdentifiers(person: CrewIdentityFields): string {
  return [personalIdLabel(person.personalNumber), text(person.phone)].filter(Boolean).join(' | ');
}

/** `חיים פשרל (מ.א. 5374154 | 052-8892893)` — the person, with their numbers. */
export function crewIdentity(person: CrewIdentityFields): string {
  const name = text(person.name);
  const numbers = crewIdentifiers(person);
  return numbers ? `${name} (${numbers})` : name;
}

/**
 * The mandated crew line of a dispatch order:
 * `נהג: חיים פשרל (מ.א. 5374154 | 052-8892893)`.
 *
 * Spelled once, here, so the sheet, the portrait card, the shared image and the
 * WhatsApp message all name a soldier the same way.
 */
export function crewIdentityLine(roleLabel: string, person: CrewIdentityFields): string {
  return `${text(roleLabel)}: ${crewIdentity(person)}`;
}

// ── The two tiers a *rendered* crew line is set in ───────────────────

/**
 * The heading of a crew line on a screen or an image: `נהג: בן פרץ`.
 *
 * A written order squeezes the role, the name and both numbers into one run
 * because paper has no second weight to work with. A card does. Read at arm's
 * length in a yard, the one thing that has to land first is *who* — so the name
 * is the line, and the numbers move below it. The bracketed one-run spelling is
 * still what {@link crewIdentityLine} produces, and is still what the WhatsApp
 * message and the printed sheet send.
 */
export function crewPrimaryLine(roleLabel: string, person: CrewIdentityFields): string {
  return `${text(roleLabel)}: ${text(person.name)}`;
}

/**
 * The quiet line under the name: `052-406-3968 · מ.א. 7533454`.
 *
 * The phone leads because it is the field that gets *used* — tapped, on a card,
 * to reach the driver — and the מ.א. follows it as the number the order is
 * signed for by rather than shouted in. Either may be missing, and a person the
 * unit holds neither for produces no line at all instead of a dangling dot.
 */
export function crewSecondaryLine(person: CrewIdentityFields): string {
  return [text(person.phone), personalIdLabel(person.personalNumber)].filter(Boolean).join(' · ');
}

/** `בינוני` — the licence class of a roster soldier, short enough for a row. */
function licenseLabelOf(soldier: Soldier | undefined): string {
  if (!soldier) return '';
  const category = soldier.licenseWeightCategory;
  return category ? WEIGHT_CATEGORY_SHORT_LABELS[category] ?? NO_LICENSE_LABEL : NO_LICENSE_LABEL;
}

function crewLine(
  crewRole: CrewRole,
  soldierId: string | undefined,
  soldiers: readonly Soldier[],
): DispatchCrewLine | null {
  if (!soldierId) return null;
  const soldier = soldiers.find((entry) => entry.id === soldierId);
  return {
    crewRole,
    roleLabel: CREW_ROLE_LABELS[crewRole],
    name: soldier?.fullName ?? soldierId,
    personalNumber: soldier?.personalNumber,
    phone: soldier?.phone,
    jobLabel: soldier ? soldierRoleLabel(soldier.role) : '',
    licenseLabel: licenseLabelOf(soldier),
    external: false,
    vehicleModel: '',
    plateNumber: '',
  };
}

/**
 * One person off the roster, in whichever seat they were placed. The unit holds
 * no licence for them, so `licenseLabel` is empty by definition — and the
 * vehicle they arrived in travels with them rather than with the fleet.
 */
function externalCrewLine(
  crewRole: CrewRole,
  person: ExternalPerson | null | undefined,
): DispatchCrewLine | null {
  if (!person) return null;
  return {
    crewRole,
    roleLabel: CREW_ROLE_LABELS[crewRole],
    name: text(person.fullName) || '—',
    personalNumber: person.personalNumber,
    phone: person.phone,
    jobLabel: text(person.role),
    licenseLabel: '',
    external: true,
    vehicleModel: text(person.vehicleModel),
    plateNumber: text(person.plateNumber),
  };
}

/** Every passenger of a team, roster and external alike, in team order. */
function passengerLines(team: TaskTeam, soldiers: readonly Soldier[]): DispatchCrewLine[] {
  return team.passengers.map((member) => {
    if (member.soldierId) {
      return (
        crewLine('passenger', member.soldierId, soldiers) ?? {
          crewRole: 'passenger' as CrewRole,
          roleLabel: CREW_ROLE_LABELS.passenger,
          name: member.soldierId,
          jobLabel: '',
          licenseLabel: '',
          external: false,
          vehicleModel: '',
          plateNumber: '',
        }
      );
    }
    return (
      externalCrewLine('passenger', member.externalPassenger) ?? {
        crewRole: 'passenger' as CrewRole,
        roleLabel: CREW_ROLE_LABELS.passenger,
        name: '—',
        jobLabel: '',
        licenseLabel: '',
        external: true,
        vehicleModel: '',
        plateNumber: '',
      }
    );
  });
}

function teamLine(
  team: TaskTeam,
  vehicles: readonly Vehicle[],
  soldiers: readonly Soldier[],
): DispatchTeamLine {
  const vehicle = vehicles.find((entry) => entry.id === team.vehicleId);
  // Each seat is asked of the roster first and of the external slot second —
  // the same order `teamCrewMembers()` materialises them in, so the sheet, the
  // headcount and the order can never disagree about who is driving.
  const escort =
    team.escortOption === 'soldier'
      ? crewLine('escort', team.escortId, soldiers)
      : team.escortOption === 'external'
        ? externalCrewLine('escort', team.externalEscort)
        : null;
  return {
    teamId: team.id,
    vehicleId: team.vehicleId,
    vehicleMilitaryId: vehicle ? vehicleCallSign(vehicle) || '—' : '—',
    vehicleName: vehicle?.name ?? '—',
    vehicleModel: text(vehicle?.model),
    plateNumber: vehicle?.plateNumber ?? '',
    vehicleTypeLabel: vehicle ? VEHICLE_TYPE_LABELS[vehicle.vehicleType] : '',
    // The company holding the vehicle right now, not the one that owns it —
    // the same answer the fleet badge paints.
    vehicleCompany: vehicle ? describeVehicleLocation(vehicle).company : '',
    driver: crewLine('driver', team.driverId, soldiers) ?? externalCrewLine('driver', team.externalDriver),
    escort,
    escortLabel: escort?.name ?? ESCORT_OPTION_LABELS[team.escortOption],
    passengers: passengerLines(team, soldiers),
    crewCount: teamHeadcount(team),
    teamNote: text(team.teamNote) || undefined,
  };
}

function dispatchRow(task: Task, input: Required<Pick<DispatchInput, 'teams'>> & DispatchInput): DispatchRow {
  const vehicles = input.vehicles ?? [];
  const soldiers = input.soldiers ?? [];
  const contact = contactOf(task, input.contacts ?? []);
  const teams = input.teams
    .filter((team) => team.taskId === task.id)
    .map((team) => teamLine(team, vehicles, soldiers));

  return {
    taskId: task.id,
    title: task.title,
    typeName: taskTypeName(task.taskTypeId, input.taskTypes ?? []),
    date: task.date,
    dateLabel: formatTaskDate(task.date, input.now ?? Date.now()),
    window: formatTaskWindow(task),
    startTime: task.startTime,
    dayPart: dayPartOf(task.startTime),
    priority: task.priority,
    priorityLabel: TASK_PRIORITY_LABELS[task.priority],
    statusLabel: taskStatusLabel(task.statusId),
    destination: text(task.destinationAddress),
    destinationLink: text(task.destinationLink),
    contactName: text(contact?.name),
    contactPhone: text(contact?.phone),
    contactRole: text(contact?.role),
    operationalNotes: text(task.operationalNotes),
    teams,
    vehicleCount: teams.length,
    crewCount: teams.reduce((total, team) => total + team.crewCount, 0),
  };
}

/** Groups the rows the way the sheet was asked for, keeping empty groups out. */
function groupRows(rows: readonly DispatchRow[], groupBy: DispatchGrouping): DispatchGroup[] {
  if (groupBy === 'time') {
    return DAY_PARTS.map((part) => ({
      key: part,
      title: DAY_PART_LABELS[part],
      rows: rows.filter((row) => row.dayPart === part),
    })).filter((group) => group.rows.length > 0);
  }

  const groups: DispatchGroup[] = [];
  for (const row of rows) {
    const title = row.typeName || 'ללא סוג משימה';
    const existing = groups.find((group) => group.key === title);
    if (existing) existing.rows.push(row);
    else groups.push({ key: title, title, rows: [row] });
  }
  return groups;
}

/**
 * The whole sheet: the tasks inside the range, each with its convoy, grouped
 * either by task type or by the part of the day it starts in.
 */
export function buildDispatchReport(input: DispatchInput): DispatchReport {
  const range = normalizeRange(input.range?.from, input.range?.to);
  const groupBy = input.groupBy ?? DEFAULT_DISPATCH_GROUPING;
  const teams = input.teams ?? [];

  const tasks = filterDispatchTasks(input.tasks, range, {
    includeCancelled: input.includeCancelled,
  });
  const rows = tasks.map((task) => dispatchRow(task, { ...input, teams }));

  return {
    range,
    rangeLabel: formatRangeLabel(range),
    groupBy,
    groups: groupRows(rows, groupBy),
    rows,
    totals: {
      tasks: rows.length,
      vehicles: rows.reduce((total, row) => total + row.vehicleCount, 0),
      crew: rows.reduce((total, row) => total + row.crewCount, 0),
    },
  };
}
